/**
 * Voice dictation for the entry field (P6-01) via the Web Speech API (Chrome / Edge / Safari 14.1+).
 * `supported === false` → the mic button is hidden (graceful fallback, no polyfill).
 */
import { useCallback, useEffect, useRef, useState } from 'react';

function getRecognition() {
  if (typeof window === 'undefined') return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

export function useSpeechInput({ onResult, lang = 'en-US' } = {}) {
  const Ctor = getRecognition();
  const supported = Boolean(Ctor);
  const [listening, setListening] = useState(false);
  const [error, setError] = useState(null);
  const recRef = useRef(null);
  const onResultRef = useRef(onResult);
  useEffect(() => { onResultRef.current = onResult; }, [onResult]);

  const stop = useCallback(() => {
    try { recRef.current?.stop(); } catch { /* already stopped */ }
    setListening(false);
  }, []);

  const start = useCallback(() => {
    if (!Ctor) return;
    setError(null);
    const rec = new Ctor();
    rec.lang = lang;
    rec.interimResults = true;
    rec.continuous = false;
    rec.onresult = (e) => {
      let text = '';
      for (let i = 0; i < e.results.length; i += 1) text += e.results[i][0].transcript;
      onResultRef.current?.(text.trim(), e.results[e.results.length - 1]?.isFinal === true);
    };
    rec.onerror = (e) => { setError(e.error || 'speech-error'); setListening(false); };
    rec.onend = () => setListening(false);
    recRef.current = rec;
    try { rec.start(); setListening(true); } catch (err) { setError(err?.message || 'speech-error'); }
  }, [Ctor, lang]);

  useEffect(() => () => { try { recRef.current?.abort(); } catch { /* noop */ } }, []);

  return { supported, listening, error, start, stop, toggle: listening ? stop : start };
}

export default useSpeechInput;
